import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

/**
 * useBottomNavData
 * Fetches all data needed by the mobile bottom navigation drawers.
 *
 * Correct schema (verified against actual DB errors):
 *  - games          : id, game_date, court, status, season_id,
 *                     game_type, team1_id, team2_id, team1_score, team2_score,
 *                     counts_in_standings
 *                     (NO game_time column)
 *                     team1:teams!games_team1_id_fkey, team2:teams!games_team2_id_fkey
 *  - teams          : id, name, wins, losses, primary_color, secondary_color, gradient, captain_id, season_id
 *  - players        : id, name, jersey_number, team_id, profile_id, season_id
 *  - game_stats     : player_id, game_id, points, rebounds, assists, steals,
 *                     two_pm, three_pm, ft_made, personal_fouls, technical_fouls
 *                     (NO blocks column)
 *  - profiles       : id, name, avatar_url
 *  - player_career_stats : profile_id, games_played, points, rebounds, assists, steals, blocks
 *
 * Key fixes vs original:
 *  - No scheduled_games table → use games
 *  - No team_players table → use players with profile_id FK
 *  - No player_stats_summary view → aggregate from game_stats
 *  - No game_rsvps table → RSVP is no-op
 *  - teams.color doesn't exist → use primary_color / secondary_color
 *  - game.location doesn't exist → use game.court
 *  - Season filter on game_stats must use games!inner join, not dot-notation
 *  - Upcoming games: don't filter by team1_score IS NULL (unreliable); use date only
 */
export const useBottomNavData = ({
  user,
  isAdmin,
  isCaptain,
  isPlayer,
  activeSeason,
}) => {
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [myTeam, setMyTeam] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [myLastGame, setMyLastGame] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const seasonId = activeSeason?.id ?? null;
  const userId = user?.id ?? null;

  // Resolved player/team for the active season (set by resolvePlayerRecord)
  const [resolvedTeamId, setResolvedTeamId] = useState(null);
  const [resolvedPlayerId, setResolvedPlayerId] = useState(null);

  // ── Step 1: resolve the player's team for the active season ─────────────────
  // Look up via players.profile_id rather than trusting profiles.team_id,
  // because a user can be admin+captain+player with different team contexts.
  const resolvePlayerRecord = useCallback(async () => {
    if (!userId || !seasonId) {
      setResolvedTeamId(null);
      setResolvedPlayerId(null);
      return;
    }

    const { data, error } = await supabase
      .from("players")
      .select("id, team_id")
      .eq("profile_id", userId)
      .eq("season_id", seasonId)
      .maybeSingle();

    if (!error && data) {
      setResolvedTeamId(data.team_id ?? null);
      setResolvedPlayerId(data.id ?? null);
      return;
    }

    // Fallback for captains who may not have a player row
    if (isCaptain) {
      const { data: captainTeam } = await supabase
        .from("teams")
        .select("id")
        .eq("captain_id", userId)
        .eq("season_id", seasonId)
        .maybeSingle();
      setResolvedTeamId(captainTeam?.id ?? null);
    } else {
      setResolvedTeamId(null);
    }
    setResolvedPlayerId(null);
  }, [userId, seasonId, isCaptain]);

  // ── Upcoming games ───────────────────────────────────────────────────────────
  // FIX: Do NOT filter by team1_score IS NULL — that column may be 0 or absent
  // for unplayed games. Just filter by date >= today and season.
  // FIX: Match Schedule.jsx exactly — filter by game_type IN ["regular","playoff"],
  // filter by season_id, filter game_date >= today in JS (not query) to avoid
  // secondary order on nullable game_time column causing silent errors.
  const fetchUpcomingGames = useCallback(async () => {
    if (!seasonId) return;
    const today = new Date().toISOString().split("T")[0];

    // Match Schedule.jsx exactly: select *, game_type IN filter, order by game_date only
    const { data, error } = await supabase
      .from("games")
      .select(
        `id, game_date, court, status, season_id, game_type,
         team1:teams!games_team1_id_fkey(id, name, primary_color, secondary_color, gradient, logo_url),
         team2:teams!games_team2_id_fkey(id, name, primary_color, secondary_color, gradient, logo_url)`,
      )
      .eq("season_id", seasonId)
      .in("game_type", ["regular", "playoff"])
      .gte("game_date", today)
      .order("game_date", { ascending: true })
      .limit(20);

    if (!error && data) setUpcomingGames(data);
    else if (error)
      console.error("[BottomNav] fetchUpcomingGames error:", error);
  }, [seasonId]);

  // ── My team + roster ─────────────────────────────────────────────────────────
  const fetchMyTeam = useCallback(async () => {
    if (!resolvedTeamId || !seasonId) return;

    const { data: teamData, error: teamError } = await supabase
      .from("teams")
      .select(
        "id, name, wins, losses, primary_color, secondary_color, gradient, logo_url, captain_id",
      )
      .eq("id", resolvedTeamId)
      .maybeSingle();

    if (teamError || !teamData) return;

    // Roster: players table, filtered by team_id + season_id
    const { data: rosterData } = await supabase
      .from("players")
      .select(
        `id, name, jersey_number, profile_id,
         profile:profiles!players_profile_id_fkey(id, name, avatar_url, avatar_crop)`,
      )
      .eq("team_id", resolvedTeamId)
      .eq("season_id", seasonId)
      .limit(12);

    // Next game for this team (date >= today)
    // Use filter() instead of or() — avoids PostgREST 400 when combining or() with gte()
    const today = new Date().toISOString().split("T")[0];
    const { data: nextGameData, error: nextGameError } = await supabase
      .from("games")
      .select(
        `id, game_date, court,
         team1:teams!games_team1_id_fkey(id, name),
         team2:teams!games_team2_id_fkey(id, name)`,
      )
      .eq("season_id", seasonId)
      .gte("game_date", today)
      .or(`team1_id.eq.${resolvedTeamId},team2_id.eq.${resolvedTeamId}`)
      .order("game_date", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (nextGameError)
      console.error("[BottomNav] fetchMyTeam nextGame error:", nextGameError);

    setMyTeam({
      ...teamData,
      roster: rosterData || [],
      nextGame: nextGameData || null,
    });
  }, [resolvedTeamId, seasonId]);

  // ── Personal stats — aggregate from game_stats for this season ───────────────
  // FIX: Use games!inner join (same pattern as PlayerDashboard.jsx) so the
  // season filter is applied correctly via PostgREST embedded resource filtering.
  const fetchMyStats = useCallback(async () => {
    if (!userId || !seasonId) return;

    // Step 1: find all player IDs for this profile (same approach as PlayerDashboard.jsx line 563-569)
    // This handles admins/captains who may not have a player row for the active season
    // but DO have stats under a player row from a previous or parallel season entry.
    const playerIdToUse = resolvedPlayerId;

    // If no resolved player ID, look up all player rows for this profile and find
    // any that have stats for the current season (same as PlayerDashboard pattern)
    let playerIds = playerIdToUse ? [playerIdToUse] : [];

    if (!playerIdToUse) {
      const { data: linkedPlayers } = await supabase
        .from("players")
        .select("id, season_id")
        .eq("profile_id", userId);
      if (!linkedPlayers?.length) {
        // No player rows at all — fall back to career stats
        const { data: career } = await supabase
          .from("player_career_stats")
          .select("games_played, points, rebounds, assists, steals, blocks")
          .eq("profile_id", userId)
          .maybeSingle();
        if (career && (career.games_played ?? 0) > 0) {
          const gp = career.games_played;
          setMyStats({
            games_played: gp,
            points: career.points ?? 0,
            rebounds: career.rebounds ?? 0,
            assists: career.assists ?? 0,
            steals: career.steals ?? 0,
            blocks: career.blocks ?? 0,
            ppg: ((career.points ?? 0) / gp).toFixed(1),
            rpg: ((career.rebounds ?? 0) / gp).toFixed(1),
            apg: ((career.assists ?? 0) / gp).toFixed(1),
            spg: ((career.steals ?? 0) / gp).toFixed(1),
            bpg: ((career.blocks ?? 0) / gp).toFixed(1),
            fgp: null,
            source: "career",
          });
        }
        return;
      }
      playerIds = linkedPlayers.map((p) => p.id);
    }

    // Step 2: fetch game_stats — use !inner so the embedded filter works as SQL WHERE
    // (without !inner, PostgREST may return 400 on the embedded resource filter)
    // Include game_date so we can identify the most recent game for "Last Game" display.
    const { data: rows, error } = await supabase
      .from("game_stats")
      .select(
        `player_id, points, rebounds, assists, steals,
         two_pm, three_pm, ft_made, personal_fouls, technical_fouls,
         game:games!inner(id, season_id, status, game_date)`,
      )
      .in("player_id", playerIds)
      .eq("game.season_id", seasonId);

    if (error) {
      console.error("[BottomNav] fetchMyStats error:", error);
      return;
    }
    if (!rows) return;

    // Filter finished games in JS (same as Stats.jsx line 92-99)
    // Also exclude DNPs (all counting stats zero)
    const seasonRows = rows.filter((r) => {
      if (!r.game || r.game.status !== "finished") return false;
      const isDNP =
        (r.points ?? 0) === 0 &&
        (r.rebounds ?? 0) === 0 &&
        (r.assists ?? 0) === 0 &&
        (r.two_pm ?? 0) === 0 &&
        (r.three_pm ?? 0) === 0 &&
        (r.ft_made ?? 0) === 0 &&
        (r.personal_fouls ?? 0) === 0 &&
        (r.technical_fouls ?? 0) === 0;
      return !isDNP;
    });

    if (!seasonRows.length) return;

    // ── Last game: most recent finished row by game_date ─────────────────────
    // Sort descending by game_date (stored on the embedded game object)
    const sortedRows = [...seasonRows].sort((a, b) => {
      const da = a.game?.game_date ? new Date(a.game.game_date) : 0;
      const db = b.game?.game_date ? new Date(b.game.game_date) : 0;
      return db - da;
    });
    const lg = sortedRows[0];
    if (lg) {
      const lgPts =
        (lg.two_pm ?? 0) * 2 + (lg.three_pm ?? 0) * 3 + (lg.ft_made ?? 0);
      const lgDateStr = lg.game?.game_date
        ? new Date(
            String(lg.game.game_date).slice(0, 10) + "T12:00:00",
          ).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : null;
      setMyLastGame({
        pts: lgPts,
        reb: lg.rebounds ?? 0,
        ast: lg.assists ?? 0,
        stl: lg.steals ?? 0,
        dateStr: lgDateStr,
      });
    }

    const gp = seasonRows.length;
    const totals = seasonRows.reduce(
      (acc, r) => {
        // points is always derived: 2pm*2 + 3pm*3 + ftm (never stored directly)
        acc.pts += r.two_pm * 2 + r.three_pm * 3 + r.ft_made;
        acc.reb += r.rebounds ?? 0;
        acc.ast += r.assists ?? 0;
        acc.stl += r.steals ?? 0;
        acc.blk += 0; // blocks not in game_stats schema
        acc.fgm += (r.two_pm ?? 0) + (r.three_pm ?? 0);
        acc.fga += (r.two_pm ?? 0) + (r.three_pm ?? 0); // simplified (no missed shots stored)
        return acc;
      },
      { pts: 0, reb: 0, ast: 0, stl: 0, blk: 0, fgm: 0, fga: 0 },
    );

    setMyStats({
      games_played: gp,
      points: totals.pts,
      rebounds: totals.reb,
      assists: totals.ast,
      steals: totals.stl,
      blocks: totals.blk,
      ppg: (totals.pts / gp).toFixed(1),
      rpg: (totals.reb / gp).toFixed(1),
      apg: (totals.ast / gp).toFixed(1),
      spg: (totals.stl / gp).toFixed(1),
      bpg: (totals.blk / gp).toFixed(1),
      fgp: totals.fga > 0 ? totals.fgm / totals.fga : null,
      source: "season",
    });
  }, [resolvedPlayerId, userId, seasonId]);

  // ── Leaderboard — top 10 PPG for the active season ──────────────────────────
  // FIX: Use games!inner join with season_id filter (same pattern as Stats.jsx)
  const fetchLeaderboard = useCallback(async () => {
    if (!seasonId) return;

    // FIX: Match Stats.jsx exactly — aliased join with explicit FK name,
    // filter by alias dot-notation, filter status in JS
    // Use !inner so the embedded season filter works as SQL WHERE (avoids 400)
    const { data: rows, error } = await supabase
      .from("game_stats")
      .select(
        `player_id, rebounds, assists, two_pm, three_pm, ft_made,
         game:games!inner(id, season_id, status, game_type),
         player:players!game_stats_player_id_fkey(
           id, name, jersey_number, profile_id, team_id,
           team:teams!players_team_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)
         )`,
      )
      .eq("game.season_id", seasonId)
      .limit(2000);

    if (error) {
      console.error("[BottomNav] fetchLeaderboard error:", error);
      return;
    }
    if (!rows) return;

    // Aggregate per player_id — filter finished games + exclude DNPs in JS
    const playerMap = {};
    rows.forEach((r) => {
      if (!r.player) return;
      // Only count finished games (same as Stats.jsx)
      if (!r.game || r.game.status !== "finished") return;
      const isDNP =
        (r.rebounds ?? 0) === 0 &&
        (r.assists ?? 0) === 0 &&
        (r.two_pm ?? 0) === 0 &&
        (r.three_pm ?? 0) === 0 &&
        (r.ft_made ?? 0) === 0;
      if (isDNP) return;

      const pid = r.player_id;
      if (!playerMap[pid]) {
        playerMap[pid] = {
          player_id: pid,
          profile_id: r.player.profile_id,
          // Use players.name directly — no nested profile join needed
          name: r.player.name,
          jersey_number: r.player.jersey_number,
          team_id: r.player.team_id,
          team: r.player.team ?? null,
          gp: 0,
          pts: 0,
          reb: 0,
          ast: 0,
        };
      }
      const p = playerMap[pid];
      p.gp += 1;
      // Points are always derived: 2pm*2 + 3pm*3 + ftm
      p.pts += (r.two_pm ?? 0) * 2 + (r.three_pm ?? 0) * 3 + (r.ft_made ?? 0);
      p.reb += r.rebounds ?? 0;
      p.ast += r.assists ?? 0;
    });

    const leaders = Object.values(playerMap)
      .filter((p) => p.gp >= 1)
      .map((p) => ({
        player_id: p.player_id,
        profile_id: p.profile_id,
        name: p.name,
        jersey_number: p.jersey_number,
        team_id: p.team_id,
        team: p.team,
        ppg: p.gp > 0 ? p.pts / p.gp : 0,
        rpg: p.gp > 0 ? p.reb / p.gp : 0,
        apg: p.gp > 0 ? p.ast / p.gp : 0,
      }))
      .sort((a, b) => b.ppg - a.ppg)
      .slice(0, 10);

    setLeaderboard(leaders);
  }, [seasonId]);

  // ── Notifications (role-aware) ───────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    const notes = [];

    if (isAdmin) {
      const { count: adjCount } = await supabase
        .from("stat_adjustment_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      if (adjCount > 0) {
        notes.push({
          id: "adj",
          type: "admin",
          icon: "clipboard",
          title: "Score Approvals Pending",
          body: `${adjCount} stat adjustment request${adjCount > 1 ? "s" : ""} awaiting review`,
          action: "/admin?tab=adjustments",
          priority: 1,
        });
      }

      const { count: regCount } = await supabase
        .from("registration_submissions")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      if (regCount > 0) {
        notes.push({
          id: "reg",
          type: "admin",
          icon: "users",
          title: "Pending Registrations",
          body: `${regCount} team registration${regCount > 1 ? "s" : ""} need approval`,
          action: "/admin?tab=regs",
          priority: 2,
        });
      }
    }

    if (isCaptain && seasonId && resolvedTeamId) {
      const today = new Date().toISOString().split("T")[0];
      const { data: nextGame } = await supabase
        .from("games")
        .select("id, game_date")
        .eq("season_id", seasonId)
        .or(`team1_id.eq.${resolvedTeamId},team2_id.eq.${resolvedTeamId}`)
        .gte("game_date", today)
        .order("game_date", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (nextGame) {
        const gameDate = new Date(nextGame.game_date + "T00:00");
        const hoursUntil = (gameDate - Date.now()) / 3600000;
        if (hoursUntil < 48) {
          notes.push({
            id: "lineup",
            type: "captain",
            icon: "clipboard-list",
            title: "Lineup Due Soon",
            body: "Submit your lineup before your next game",
            action: "/captain",
            priority: 1,
          });
        }
      }
    }

    if ((isPlayer || isCaptain) && resolvedTeamId && seasonId) {
      const today = new Date().toISOString().split("T")[0];
      const { data: nextGame } = await supabase
        .from("games")
        .select(
          `id, game_date, court,
           team1:teams!games_team1_id_fkey(id, name),
           team2:teams!games_team2_id_fkey(id, name)`,
        )
        .eq("season_id", seasonId)
        .or(`team1_id.eq.${resolvedTeamId},team2_id.eq.${resolvedTeamId}`)
        .gte("game_date", today)
        .order("game_date", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (nextGame) {
        const gameDate = new Date(nextGame.game_date + "T00:00");
        const hoursUntil = (gameDate - Date.now()) / 3600000;
        if (hoursUntil < 72) {
          const myTeamName = myTeam?.name;
          const opponent =
            nextGame.team1?.name === myTeamName
              ? nextGame.team2?.name
              : nextGame.team1?.name;
          notes.push({
            id: "game-reminder",
            type: "player",
            icon: "basketball",
            title: "Game Day Approaching",
            body: `vs ${opponent || "TBD"} — ${nextGame.court || "TBD"}`,
            action: "/schedule",
            priority: 1,
          });
        }
      }
    }

    setNotifications(notes);
  }, [
    userId,
    isAdmin,
    isCaptain,
    isPlayer,
    resolvedTeamId,
    seasonId,
    myTeam?.name,
  ]);

  // ── Step 1: resolve player record whenever user/season changes ───────────────
  useEffect(() => {
    resolvePlayerRecord();
  }, [resolvePlayerRecord]);

  // ── Step 2: fetch all data once player record is resolved ────────────────────
  useEffect(() => {
    if (!seasonId) return;
    setLoading(true);
    Promise.all([
      fetchUpcomingGames(),
      fetchMyTeam(),
      fetchMyStats(),
      fetchLeaderboard(),
    ]).finally(() => setLoading(false));
  }, [
    fetchUpcomingGames,
    fetchMyTeam,
    fetchMyStats,
    fetchLeaderboard,
    seasonId,
    resolvedTeamId,
    resolvedPlayerId,
  ]);

  // ── Step 3: notifications depend on myTeam name for opponent label ───────────
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    upcomingGames,
    myTeam,
    myStats,
    myLastGame,
    leaderboard,
    notifications,
    rsvpMap: {},
    toggleRsvp: () => {},
    loading,
    resolvedTeamId,
    resolvedPlayerId,
    refetch: () => {
      fetchUpcomingGames();
      fetchMyTeam();
      fetchMyStats();
      fetchLeaderboard();
      fetchNotifications();
    },
  };
};
